/**
 * Arabic (RTL) SEO landing page at /ar.
 *
 * The app UI is English; this is a real Arabic-language page so Google has
 * something to index for Arabic search and the hreflang alternates on the home
 * page have a valid counterpart to point at. Copy is accessible Arabic with a
 * Lebanese tone. CTAs lead into the (English) app.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useHreflang, SEO_ORIGIN } from '@/components/common/Seo';

export const ArabicLandingPage: React.FC = () => {
  useDocumentMeta({
    title: 'دارك | بيوت وعطل للإيجار بلبنان',
    description:
      'احجز بيوت وشاليهات وعطل بكل لبنان، من بترون وجبيل لفاريا وإهدن. صور حقيقية، أسعار واضحة، دفع بالكارت أو Whish، وتواصل مباشر مع المضيف على دارك.',
    canonical: `${SEO_ORIGIN}/ar`,
  });
  useHreflang('/', '/ar', 'ar');

  return (
    <div dir="rtl" lang="ar" className="min-h-screen bg-white text-gray-900">
      <main className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="text-3xl font-bold mb-3">دارك: بيوت وعطل بلبنان</h1>
        <p className="text-lg text-gray-600 mb-8">
          لاقِ بيت العطلة يلّي بيناسبك بكل لبنان، من بترون لفاريا. احجز بأمان،
          ادفع بالكارت أو بالـWhish، وتواصل مع صاحب البيت دغري من التطبيق.
        </p>

        <div className="flex flex-wrap gap-3 mb-12">
          <Link
            to="/search"
            className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors"
          >
            دوّر على بيت
          </Link>
          <Link
            to="/become-host"
            className="px-6 py-3 border border-gray-300 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
          >
            استضِف بيتك
          </Link>
        </div>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">للضيوف</h2>
          <ul className="list-disc pr-5 space-y-2 text-gray-700">
            <li>دوّر وفلتِر حسب المنطقة، النوع، السعر، وعدد الضيوف.</li>
            <li>شوف صور حقيقية، الأسعار، وشروط كل بيت قبل ما تحجز.</li>
            <li>احجز أو ابعت طلب حجز، وتابع حالتو خطوة بخطوة.</li>
            <li>ادفع بالطريقة يلّي بتريّحك: كارت أو Whish Money.</li>
            <li>تواصل مع المضيف دغري من الحجز.</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">للمضيفين</h2>
          <ul className="list-disc pr-5 space-y-2 text-gray-700">
            <li>حطّ بيتك بالكامل من التطبيق: صور، سعر، وأيام نهاية الأسبوع يلّي بتختارها.</li>
            <li>سعّر بالدولار، اليورو، أو الليرة اللبنانية.</li>
            <li>اقبل أو ارفض الطلبات، وضبّط قوانين بيتك.</li>
            <li>تابع حجوزاتك وأرباحك، واطلب الدفعة لمّا تجهز.</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">أمان وثقة</h2>
          <ul className="list-disc pr-5 space-y-2 text-gray-700">
            <li>حسابات موثّقة والدفع قبل الإقامة.</li>
            <li>أدوات للتبليغ عن أي محتوى، وحظر أي مستخدم، مع إزالة فورية من صفحتك.</li>
            <li>فريق دعم بلبنان جاهز يساعدك.</li>
          </ul>
        </section>

        <p className="text-gray-600">
          <Link to="/" className="text-primary-600 hover:underline">English</Link>
        </p>
      </main>
    </div>
  );
};

export default ArabicLandingPage;
